import { useState, useEffect, useRef } from "react";

const INITIAL = {
  mobile: "",
  email: "",
  vehicle: "",
  vin: "",
  concerns: "",
  maintainedAt: "",
  heardFrom: "",
  bestTime: "",
};

const FIELDS = [
  { name: "mobile", label: "Mobile", type: "tel", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "vehicle", label: "Vehicle Details", type: "text", required: true },
  { name: "vin", label: "VIN Number", type: "text" },
  { name: "concerns", label: "Concerns", type: "textarea" },
  { name: "maintainedAt", label: "Where have you been maintaining your cars?", type: "text" },
  { name: "heardFrom", label: "How did you hear about us", type: "text" },
  { name: "bestTime", label: "When’s the best time and day to reach you?", type: "text" },
];

const inputClass =
  "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 rounded-md focus:outline-none focus:border-red-500 transition-colors";

export default function InspectionModal({ onClose, title = "Book Mechanical Inspection" }) {
  const [form, setForm] = useState(INITIAL);
  const [booked, setBooked] = useState(false);
  const timer = useRef(null);

  // Clear pending timer if the modal unmounts early
  useEffect(() => () => clearTimeout(timer.current), []);

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const canSubmit = form.mobile.trim() && form.email.trim() && form.vehicle.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBooked(true);
    timer.current = setTimeout(() => {
      setForm(INITIAL);
      setBooked(false);
      onClose();
    }, 3000);
  };

  return (
    <div
      onClick={booked ? undefined : onClose}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[92vh] flex flex-col bg-[#111118] border border-red-500/20 rounded-2xl shadow-2xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-bold text-white">
            {booked ? "Booked" : title}
          </h3>
          {!booked && (
            <button
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 text-white/60 hover:text-white"
            >
              ✕
            </button>
          )}
        </div>

        {booked ? (
          <div className="text-center px-6 py-12">
            <div className="text-5xl mb-4">✅</div>
            <p className="text-white text-lg font-semibold">Booked!</p>
            <p className="text-white/50 text-sm mt-1">
              Your inspection request has been received. We’ll contact you shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="overflow-y-auto px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            {FIELDS.map((f) => (
              <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                <label htmlFor={f.name} className="block text-xs text-white/60 mb-1">
                  {f.label}
                  {f.required && <span className="text-red-500"> *</span>}
                </label>
                {f.type === "textarea" ? (
                  <textarea
                    id={f.name}
                    name={f.name}
                    rows={2}
                    value={form[f.name]}
                    onChange={handleChange}
                    className={inputClass}
                  />
                ) : (
                  <input
                    id={f.name}
                    name={f.name}
                    type={f.type}
                    required={f.required}
                    value={form[f.name]}
                    onChange={handleChange}
                    className={inputClass}
                  />
                )}
              </div>
            ))}
            <button
              type="submit"
              disabled={!canSubmit}
              className="md:col-span-2 w-full bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-md transition-colors"
            >
              Submit
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
